import dayjs from "dayjs";
import { countries, COUNTRY_CODES } from "../data/data";
import packageJson from '../../package.json';
import { CountryCurrency } from "../types/countryCurrency";
import { ENV_TYPES } from "../types/env.types";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { LS } from "../data/localStoreKeys";
import { DiscountType } from "../types/DiscountTypes";
dayjs.extend(customParseFormat);

export const activeCountry = import.meta.env.VITE_COUNTRY;
export const getCurrency = CountryCurrency[activeCountry as keyof typeof CountryCurrency];


/**
 * Get the environment type based on the current environment string
 * @returns {string} The environment type
 * @throws {Error} If the environment is invalid
 */
export const getEnvironmentType = (): string => {
  const env = import.meta.env.VITE_ENV.toUpperCase();

  if (env === "DEV") {
    return ENV_TYPES.DEVELOPMENT;
  }

  if (env === "STAGING") {
    return ENV_TYPES.STAGING;
  }

  if (env === "PROD") {
    return ENV_TYPES.PRODUCTION;
  }

  throw new Error(`Invalid environment: ${env}. Application cannot run.`);
}


export const getLocalStorageRawData = <T>(keys: (keyof T)[]): Partial<Record<keyof T, string>> => {
  const data: Partial<Record<keyof T, string>> = {};

  keys.forEach((key) => {
    data[key] = sessionStorage.getItem(key as string) ?? "";
  });

  return data;
};

export const getLocalStorageParsedData = <T>(keys: (keyof T)[]): Partial<Record<keyof T, any>> => {
  const data: Partial<Record<keyof T, any>> = {};

  keys.forEach((key) => {
    const item = sessionStorage.getItem(key as string);
    data[key] = item ? JSON.parse(item) : null;
  });

  return data;
};

export const getAppVersion = () => {
  return packageJson.version;
}

export const getActiveCountry = () => {
  return activeCountry;
}

export const getBrowsingCountry = () => {
  return window.COUNTRY;
}

export const isYearlyDiscount = (type?: DiscountType): boolean => {
  return (
    type === DiscountType.YEAR_PERCENTAGE ||
    type === DiscountType.YEAR_AMOUNT
  );
};

export const normalizeDiscountType = (type?: DiscountType | string | { S: string }): DiscountType | undefined => {
    if (!type) return undefined;

    if (typeof type === "string") {
      return type as DiscountType;
    }

    if (typeof type === "object" && "S" in type) {
      return type.S as DiscountType;
    }

    return undefined;
  };


export const getWebsiteLinkBasedOnBrowsingCountry = () => {
  const env = getEnvironmentType();
  const COUNTRY = getBrowsingCountry();

  const BASE_URLS: Record<string, string> = {
    [COUNTRY_CODES.NO]: "https://flexistore.no/",
    [COUNTRY_CODES.ZA]: "https://flexistore.co.za/",
    [COUNTRY_CODES.FI]: "https://flexistore.fi/",
    [COUNTRY_CODES.US]: "https://gostorpro.com/",
  };

  const baseUrl = BASE_URLS[COUNTRY] || BASE_URLS[COUNTRY_CODES.NO];

  if (env == ENV_TYPES.STAGING) {
    return baseUrl.replace("https://", "https://staging.");
  }

  return baseUrl;
};


/**
 * On change, need to update auth method
 * and also anywhere that needs countries array
 */
export const getCountryNameFromCountryCode = (code: string, countriesData = countries) => {
  return countriesData.find((country) => country.code === code)?.name || "";
};

export const userIsFromNorway = (userAttributes: any) => {
  return (
    userAttributes["custom:nationality"] === "no" ||
    userAttributes["custom:country"] === "no" ||
    userAttributes["custom:country"] === "Norway"
  );
};
export const userIsNotFromNorway = (userAttributes: any) => {
  const country = userAttributes["custom:country"]?.toLowerCase();
  const nationality = userAttributes["custom:nationality"]?.toLowerCase();

  return country !== "no" && country !== "norge" && country !== "norway" && nationality !== "no";
};

export const userIsFromSouthAfrica = (userAttributes: any) => {
  return userAttributes["custom:nationality"] === "za" || userAttributes["custom:country"] === "South Africa";
};

export const userIsFromUnitedStates = (userAttributes: any) => {
  return userAttributes["custom:nationality"] === "us" || userAttributes["custom:country"] === "United States";
};

export const userIsFromFinland = (userAttributes: any) => {
  return userAttributes["custom:nationality"] === "fi" || userAttributes["custom:country"] === "Finland";
}

export const userNeedsToVerifyId = (userAttributes: any): boolean => {

  // Trust backend verification override first
  if (sessionStorage.getItem(LS.TEMP_VERIFIED_WITH_SUMSUB) === "true") {
    return false;
  }
  
  if (!userAttributes || Object.keys(userAttributes).length === 0) {
    return true;
  }

  const verifiedWith = userAttributes["custom:verified_with"];

  if (!verifiedWith || verifiedWith === "") {
    return true;
  }

  let parsedVerifiedWith: any;
  
  if (Array.isArray(verifiedWith)) {
    parsedVerifiedWith = verifiedWith;
  } else {
    try {
      parsedVerifiedWith = JSON.parse(verifiedWith);
    } catch (error) {
      return true;
    }
  }

  return !Array.isArray(parsedVerifiedWith) || parsedVerifiedWith.length === 0
};


export const userHasBirthday = (userAttributes: any) => {
  if (!userAttributes) {
    return false;
  }
  return "birthdate" in userAttributes && userAttributes["birthdate"] !== "";
};

// Cognito's ID token represents the standard "address" attribute as
// { formatted: string } per the OIDC address claim shape, while the
// GetUser/userAttributes API returns it as a plain string. Normalize both.
export const getCognitoAddressString = (value: any): string => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && typeof value.formatted === "string") return value.formatted;
  return "";
};

export const userHasAddressAndPostCode = (userAttributes: any) => {
  if (!userAttributes) {
    return false;
  }

  return (
    "city" in userAttributes &&
    userAttributes["city"] !== "" &&
    "address" in userAttributes &&
    userAttributes["address"] !== "" &&
    "custom:postal_code" in userAttributes &&
    userAttributes["custom:postcode"] !== ""
  );
};

// generate wordpress htaccess rules
// function generateHtaccessRoutes() {
//   Object.values(ROUTES).forEach((route) => {
//     if (route !== "/") {
//       console.log(
//         `RewriteRule ^${
//           route === "/reserve-storage" ? route : route.slice(1, 1000)
//         } /reserve-storage/index.html [L,QSA]`,
//       );
//     }
//   });
// }

export { getSaturdaysAndSundaysOfNext15Days } from "./dateUtils";

export { getDefaultStartDate } from "./dateUtils";

// check if phone has country code
export const phoneHasCountryCode = (phone: string) => {
  return phone.startsWith("+");
};

export const addCountryCodeToPhoneNumber = (phoneNumber: string, country: string) => {
  if (!phoneNumber) return undefined;

  // Remove all whitespace
  const cleanPhone = phoneNumber.replace(/\s/g, "");

  // Return undefined if phone number is empty or too short after cleaning
  if (!cleanPhone || cleanPhone.replace(/\+/, "").length < 4) return undefined;

  // Check if phone number already has a country code
  if (cleanPhone.startsWith("+")) {
    return cleanPhone;
  }

  // Find the country code based on the country name
  const countryCodeObj = countries.find((c) => c.code === country);

  // Check if a matching country was found
  if (!countryCodeObj) {
    return;
  }

  // Add the country code to the phone number
  const countryCode = countryCodeObj.cellAreaCode;
  return `${countryCode}${cleanPhone}`;
};

export const removeCountryCodeFromPhoneNumber = (phoneNumber: string, country: string) => {
  // Find the country code based on the country name
  const countryCodeObj = countries.find((c) => c.code === country);

  // Check if a matching country was found
  if (!countryCodeObj) {
    return;
  }

  const countryCode = countryCodeObj.cellAreaCode;

  // Check if countryCode is null
  if (countryCode === null) return phoneNumber; 

  // Remove the country code from the phone number
  return phoneNumber ? phoneNumber.replace(countryCode, "") : "";
};

export const capitalizeFirstLetter = (value: string): string => {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
};

/**
 * currency format based on country
 */
export const getFormattedCost = (cost: number | string) => {
  const formatted = Number(cost).toFixed(2);
  if (activeCountry === COUNTRY_CODES.ZA) {
    return `R ${Number(formatted).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  } else if (activeCountry === COUNTRY_CODES.US) {
    return `$ ${formatted}`;
  } else if (activeCountry === COUNTRY_CODES.FI) { // added euro currency used in finland
    return `€ ${formatted}`;
  } else if (activeCountry === COUNTRY_CODES.NO){
    return `${formatted} kr`;
  } else {
    return `${formatted}`;
  }
};
